import styles from "./WizardSteps.module.scss";
import classNames from "classnames";
import { SuccessIcon } from "../../../icons/monochrome";
import _ from "lodash";
import React, { useLayoutEffect, useRef, useState, useEffect, useCallback } from "react";
import { Span, Button } from "./../../";
import { ReactComponent as WizardStepArrowLeft } from "./ic_wizard_step_arrow_left.svg";
import { ReactComponent as WizardStepArrowRight } from "./ic_wizard_step_arrow_right.svg";

const Step = ({ index, label, isActive, onTabSelected, done }) => {
    return (
        <button type="button" onClick={() => onTabSelected(index)} className={classNames(styles["step"], { [styles['active']]: isActive })} disabled={!done}>
            {done ? <SuccessIcon className={classNames(styles["done-icon"])}/> : <div className={classNames(styles["index"])}>{index}</div>}
            <Span level={16} color="text-title" className={styles.label}>{label}</Span>
        </button>
    );
};

const HoldButton = React.memo(({ children, className, style, onHold, variant = "none" }) => {
    const timeoutRef = useRef(null);
    const onHoldRef = useRef(() => {
    });
    onHoldRef.current = onHold;

    useEffect(() => {
        if (timeoutRef.current) {
            clearInterval(timeoutRef.current);
        }
    }, []);

    const capture = useCallback((e) => {
        if (!timeoutRef.current) {
            onHoldRef.current();
            e.target.setPointerCapture(e.pointerId);
            timeoutRef.current = setInterval(() => {
                onHoldRef.current();
            }, 50);
        }
    }, []);

    const release = useCallback((e) => {
        if (timeoutRef.current) {
            clearInterval(timeoutRef.current)
            e.target.releasePointerCapture(e.pointerId);
            timeoutRef.current = null;
        }
    }, []);

    return <Button variant={variant} className={classNames(styles['hold-button'], className)} style={style} onPointerDown={capture} onPointerUp={release}>{children}</Button>;
});

const WizardSteps = ({ steps, currentStepIndex, setStep, className }) => {
    const ref = useRef(null);
    const scrollDuplicatorRef = useRef(null);
    const [overflowDetails, setOverflowDetails] = useState(null);
    const isOverflow = !!overflowDetails;

    useLayoutEffect(() => {
        const handleResize = () => {
            if (ref.current && ref.current.parentNode) {
                const { width } = ref.current.getBoundingClientRect();
                const { width: parentWidth } = ref.current.parentNode.getBoundingClientRect();

                const isOverflow = width > parentWidth;
                if (isOverflow) {
                    setOverflowDetails({ thumbSize: (parentWidth / width) * 100 });
                    setTimeout(() => {
                        handleScrollSpy();
                    });
                } else {
                    setOverflowDetails(null)
                }
            }
        }

        const handleScrollSpy = () => {
            if (scrollDuplicatorRef.current) {
                const percent = ref.current.parentNode.scrollLeft / ref.current.parentNode.clientWidth;
                scrollDuplicatorRef.current.childNodes[0].style.left = `${percent * 100}%`;
            }
        };

        handleResize();

        window.addEventListener("resize", handleResize);
        ref.current.parentNode.addEventListener("scroll", handleScrollSpy);

        return () => {
            window.removeEventListener("resize", handleResize);
            ref.current.parentNode.addEventListener("scroll", handleScrollSpy);
        };
    }, [setOverflowDetails])

    useEffect(() => {
        if (overflowDetails) {
            const { width } = ref.current.getBoundingClientRect();
            const { width: parentWidth } = ref.current.parentNode.getBoundingClientRect();
            if (Math.abs((parentWidth / width) * 100 - overflowDetails.thumbSize) > 0.5) {
                setOverflowDetails({ thumbSize: (parentWidth / width) * 100 });
            }
        }

    }, [overflowDetails, setOverflowDetails]);

    useEffect(() => {
        ref.current.childNodes[currentStepIndex].scrollIntoView();
    }, [currentStepIndex])

    const leftArrowClick = useCallback(() => {
        ref.current.parentNode.scrollBy({
            top: 0,
            left: -10,
            behavior: 'smooth'
        });
    }, []);

    const rightArrowClick = useCallback(() => {
        ref.current.parentNode.scrollBy({
            top: 0,
            left: 10,
            behavior: 'smooth'
        });
    }, []);

    return <>
        {isOverflow && <HoldButton className={styles['left-arrow']} onHold={leftArrowClick}><WizardStepArrowLeft/></HoldButton>}
        <div className={classNames(styles.base, className, { [styles["overflow"]]: isOverflow })}>
            <div className={styles.steps} ref={ref}>
                {_.map(steps, ({ label, key }, index) => {
                    const isDone = index < currentStepIndex;
                    return <Step
                        key={key}
                        done={isDone}
                        onTabSelected={() => setStep(key)}
                        isActive={index === currentStepIndex}
                        label={label}
                        index={index + 1}
                    />
                })}
            </div>
        </div>
        {isOverflow && <HoldButton className={styles['right-arrow']} onHold={rightArrowClick}><WizardStepArrowRight/></HoldButton>}
        {isOverflow && <div className={styles['scroll-duplicator']} ref={scrollDuplicatorRef} onClick={(e) => {
            if (e.target === scrollDuplicatorRef.current) {
                const percent = e.nativeEvent.offsetX / scrollDuplicatorRef.current.clientWidth;
                ref.current.parentNode.scrollLeft = percent * ref.current.parentNode.clientWidth;
            }
        }}>
            <div style={{ width: `${overflowDetails.thumbSize}%` }} onPointerDown={(e) => {
                scrollDuplicatorRef.current.childNodes[0].onpointermove = (e) => {
                    ref.current.parentNode.scrollLeft = ref.current.parentNode.scrollLeft + e.movementX;
                }
                scrollDuplicatorRef.current.childNodes[0].setPointerCapture(e.pointerId);
            }} onPointerUp={(e) => {
                scrollDuplicatorRef.current.childNodes[0].onpointermove = null;
                scrollDuplicatorRef.current.childNodes[0].releasePointerCapture(e.pointerId);
            }}/>
        </div>}
    </>
}

export default WizardSteps;
